import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !project) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'details', label: 'Details', icon: 'ListChecks' },
    { id: 'stack', label: 'Stack', icon: 'Boxes' },
    { id: 'notes', label: 'Notes', icon: 'StickyNote' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="absolute inset-4 bg-white rounded-lg enterprise-shadow overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-surface">
              <Icon name={project?.icon || 'Layers'} size={22} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-primary">{project?.title}</h2>
              <p className="text-text-secondary">{project?.category} • {project?.duration}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium enterprise-transition ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5'
                  : 'text-text-secondary hover:text-primary hover:bg-surface'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              {project?.summary && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Summary</h3>
                  <p className="text-text-secondary leading-relaxed">{project?.summary}</p>
                </div>
              )}
              {project?.whatWeDid && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">What we did</h3>
                  <p className="text-text-secondary leading-relaxed">{project?.whatWeDid}</p>
                </div>
              )}
              {project?.feedback && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Icon name="MessageSquare" size={18} className="text-success mt-0.5" />
                    <blockquote className="text-text-primary italic">"{project?.feedback}"</blockquote>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-surface rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-2">Project Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-text-secondary">Duration:</span><span className="text-text-primary">{project?.duration}</span></div>
                    {project?.category && (
                      <div className="flex justify-between"><span className="text-text-secondary">Category:</span><span className="text-text-primary">{project?.category}</span></div>
                    )}
                  </div>
                </div>
                {project?.tags?.length > 0 && (
                  <div className="bg-surface rounded-lg p-4">
                    <h4 className="font-medium text-text-primary mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {project?.tags?.map((tag, idx) => (
                        <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'stack' && (
            <div className="p-6">
              <h4 className="font-medium text-text-primary mb-3">Technology Stack</h4>
              <div className="flex flex-wrap gap-2">
                {project?.stack?.map((tech, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white border border-border text-sm rounded-md">{tech}</span>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="p-6 space-y-2 text-sm text-text-secondary">
              <p>Client names anonymized unless provided. Feedback paraphrased.</p>
              {project?.title?.toLowerCase()?.includes('neurostack') && (
                <p>
                  Open source reference: <a href="https://github.com/kkahol-toronto/neurostack" target="_blank" rel="noreferrer" className="text-primary underline">github.com/kkahol-toronto/neurostack</a>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-border bg-surface">
          <Button variant="default" iconName="MessageCircle" iconPosition="left">
            Discuss Similar Project
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;


